import classNames from 'classnames'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import update from 'immutability-helper'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sharedClasses from '@modals/index.module.css'
import Button from '@UI/New/Button'
import { InputWhite } from '@UI/New/InputWhite'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import Preloader from '@UI/Preloader'
import { contractTemplateApi } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplate.api'
import type { ContractTemplate } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplate.types'
import type { ContractConstructorData } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplateConstructor.types'
import { usersAPI } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import ConstructorGroupFields from './ConstructorGroupFields'
import type { StudentContractState } from './AddContractModal'
import {
  buildConstructorRequestParams,
  buildCreateContractPayload,
  buildValuesFromGroups,
  applyAmountWordsAutofill,
  getAmountWordsFieldCode,
  getConstructorGroupFieldCodes,
  getEmptyRequiredFieldCodes,
  getRublesAmountInWords,
  getProductOptionKey,
  getSelectedProductKey,
  hasSelectableProducts,
  mergeConstructorGroupValues,
  PARENT_GROUP_CODES,
  PRODUCT_GROUP_CODES,
} from './contractTemplateConstructor.utils'
import classes from './AddTemplateContractModal.module.css'

type AddTemplateContractModalProps = {
  active?: boolean
  close?: () => void
  state: StudentContractState
  setState: (value: StudentContractState) => void
}

type LoadConstructorArgs = {
  nextTemplateId: number
  nextParentId?: number | null
  nextProductKey?: string
  includeProductParams?: boolean
  loadingScope?: 'constructor' | 'parent' | 'product'
}

const getErrorText = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback

const AddTemplateContractModal = ({
  active = true,
  close,
  state,
  setState,
}: AddTemplateContractModalProps) => {
  const addAlert = useAddAlert()
  const addAlertRef = useRef(addAlert)

  useEffect(() => {
    addAlertRef.current = addAlert
  }, [addAlert])

  const [templates, setTemplates] = useState<ContractTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [constructorLoading, setConstructorLoading] = useState(false)
  const [parentLoading, setParentLoading] = useState(false)
  const [productLoading, setProductLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [constructorData, setConstructorData] =
    useState<ContractConstructorData | null>(null)
  const [documentName, setDocumentName] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null)
  const [selectedProductKey, setSelectedProductKey] = useState('custom')
  const [invalidFieldCodes, setInvalidFieldCodes] = useState<Set<string>>(
    new Set(),
  )

  const templateOptions = useMemo(
    () =>
      templates
        .filter((template) => template.is_active !== 0)
        .map((template) => ({
          label: template.name,
          value: String(template.id),
        })),
    [templates],
  )

  const parentOptions = useMemo(
    () =>
      constructorData?.parents?.map((parent) => ({
        label: parent.name,
        value: parent.id == null ? null : String(parent.id),
      })) ?? [],
    [constructorData?.parents],
  )

  const productOptions = useMemo(
    () =>
      constructorData?.products?.map((product) => ({
        label: product.title,
        value: getProductOptionKey(product),
      })) ?? [],
    [constructorData?.products],
  )

  const shouldShowProductSelector = hasSelectableProducts(
    constructorData?.products,
  )
  const constructorGroups = constructorData?.groups ?? []

  useEffect(() => {
    if (!active) return undefined

    let isCancelled = false
    setTemplatesLoading(true)

    contractTemplateApi
      .getList()
      .then((list) => {
        if (!isCancelled) {
          setTemplates(list)
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          addAlertRef.current({
            title: 'Ошибка',
            text: getErrorText(error, 'Не удалось загрузить шаблоны договоров'),
            type: 'error',
          })
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setTemplatesLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [active])

  const loadConstructor = async ({
    nextTemplateId,
    nextParentId,
    nextProductKey = 'custom',
    includeProductParams = false,
    loadingScope = 'constructor',
  }: LoadConstructorArgs) => {
    if (!state?.id) return

    if (loadingScope === 'parent') {
      setParentLoading(true)
    } else if (loadingScope === 'product') {
      setProductLoading(true)
    } else {
      setConstructorLoading(true)
    }

    try {
      const data = await contractTemplateApi.getConstructor(
        buildConstructorRequestParams({
          template_id: Number(nextTemplateId),
          user_id: Number(state.id),
          parent_id: nextParentId,
          productKey: nextProductKey,
          includeProductParams,
        }),
      )

      const nextValues = applyAmountWordsAutofill(
        buildValuesFromGroups(data.groups),
      )

      setConstructorData(data)

      if (loadingScope === 'parent') {
        const parentFieldCodes = getConstructorGroupFieldCodes(
          data.groups,
          PARENT_GROUP_CODES,
        )
        setValues((prev) =>
          mergeConstructorGroupValues(prev, nextValues, parentFieldCodes),
        )
        setInvalidFieldCodes((prev) => {
          if (!prev.size) return prev
          const next = new Set(prev)
          parentFieldCodes.forEach((code) => next.delete(code))
          return next
        })
      } else if (loadingScope === 'product') {
        const productFieldCodes = getConstructorGroupFieldCodes(
          data.groups,
          PRODUCT_GROUP_CODES,
        )
        setValues((prev) =>
          mergeConstructorGroupValues(prev, nextValues, productFieldCodes),
        )
        setInvalidFieldCodes((prev) => {
          if (!prev.size) return prev
          const next = new Set(prev)
          productFieldCodes.forEach((code) => next.delete(code))
          return next
        })
      } else {
        setDocumentName(data.document_name || '')
        setValues(nextValues)
        setSelectedParentId(data.selected_parent_id ?? null)
        setSelectedProductKey(getSelectedProductKey(data.selected_product))
        setInvalidFieldCodes(new Set())
      }
    } catch (error) {
      addAlertRef.current({
        title: 'Ошибка',
        text: getErrorText(error, 'Не удалось загрузить поля шаблона'),
        type: 'error',
      })
    } finally {
      if (loadingScope === 'parent') {
        setParentLoading(false)
      } else if (loadingScope === 'product') {
        setProductLoading(false)
      } else {
        setConstructorLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!active || templateId == null) return

    loadConstructor({
      nextTemplateId: templateId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, templateId])

  const handleTemplateChange = (value: string | string[] | null) => {
    const rawValue = Array.isArray(value) ? value[0] ?? null : value
    const nextTemplateId =
      rawValue != null && rawValue !== '' ? Number(rawValue) : null

    setTemplateId(nextTemplateId)
    setConstructorData(null)
    setValues({})
    setSelectedParentId(null)
    setSelectedProductKey('custom')
    setInvalidFieldCodes(new Set())
  }

  const handleParentChange = (parentId: number | null) => {
    if (templateId == null) return

    setSelectedParentId(parentId)
    loadConstructor({
      nextTemplateId: templateId,
      nextParentId: parentId,
      nextProductKey: selectedProductKey,
      includeProductParams: true,
      loadingScope: 'parent',
    })
  }

  const handleProductChange = (productKey: string) => {
    if (templateId == null) return

    setSelectedProductKey(productKey)
    loadConstructor({
      nextTemplateId: templateId,
      nextParentId: selectedParentId,
      nextProductKey: productKey,
      includeProductParams: true,
      loadingScope: 'product',
    })
  }

  const handleFieldChange = (code: string, value: string) => {
    const amountWordsFieldCode = getAmountWordsFieldCode(code)
    const amountWords = amountWordsFieldCode ? getRublesAmountInWords(value) : ''

    setValues((prev) => ({
      ...prev,
      [code]: value,
      ...(amountWordsFieldCode
        ? { [amountWordsFieldCode]: amountWords }
        : {}),
    }))
    setInvalidFieldCodes((prev) => {
      if (
        !prev.has(code) &&
        (!amountWordsFieldCode || !prev.has(amountWordsFieldCode))
      ) {
        return prev
      }

      const next = new Set(prev)
      next.delete(code)
      if (amountWords && amountWordsFieldCode) {
        next.delete(amountWordsFieldCode)
      }
      return next
    })
  }

  const refreshContracts = async () => {
    const userRes = await usersAPI.getUser({
      id: state.id,
      expand: ['contracts'],
    })

    setState(
      update(state, {
        contracts: { $set: userRes.data.contracts },
      }),
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (templateId == null || !state?.id || !constructorData) {
      return
    }

    const emptyFields = getEmptyRequiredFieldCodes(constructorGroups, values)

    if (!documentName.trim()) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите название договора',
        type: 'error',
      })
      return
    }

    if (emptyFields.length) {
      setInvalidFieldCodes(new Set(emptyFields))
      addAlert({
        title: 'Ошибка',
        text: 'Заполните все поля, используемые в шаблоне договора',
        type: 'error',
      })
      return
    }

    setIsSubmitting(true)

    try {
      await contractTemplateApi.createContractFromTemplate(
        buildCreateContractPayload({
          user_id: Number(state.id),
          template_id: Number(templateId),
          document_name: documentName.trim(),
          parent_id: selectedParentId,
          productKey: selectedProductKey,
          values,
        }),
      )

      await refreshContracts()
      addAlert({ title: 'Договор успешно создан' })
      close?.()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: getErrorText(error, 'Не удалось создать договор из шаблона'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!active) {
    return null
  }

  const parentSelector =
    parentOptions.length > 0 ? (
      <SelectField
        label="Выберите родителя"
        value={selectedParentId == null ? 'null' : String(selectedParentId)}
        options={parentOptions.map((option) => ({
          label: option.label,
          value: option.value == null ? 'null' : String(option.value),
        }))}
        placeholder="Выберите родителя"
        className={classes.selectField}
        onChange={(value) => {
          if (value == null || value === 'null') {
            handleParentChange(null)
            return
          }

          handleParentChange(Number(value))
        }}
      />
    ) : null

  const productSelector = shouldShowProductSelector ? (
    <SelectField
      label="Выберите курс/класс"
      value={selectedProductKey}
      options={productOptions}
      placeholder="Выберите курс/класс"
      className={classes.selectField}
      onChange={(value) => {
        handleProductChange(String(value ?? 'custom'))
      }}
    />
  ) : null

  const getGroupSelector = (groupCode: string) => {
    if (PARENT_GROUP_CODES.has(groupCode)) {
      return parentSelector
    }

    if (PRODUCT_GROUP_CODES.has(groupCode)) {
      return productSelector
    }

    return null
  }

  const isSubmitDisabled =
    templateId == null ||
    !constructorData ||
    constructorLoading ||
    parentLoading ||
    productLoading ||
    isSubmitting ||
    !documentName.trim()

  return createPortal(
    <Modal
      active={active}
      setActive={close}
      className={classes.modalWrapper as any}
      style={{ zIndex: '110' }}
      styleContent={{
        minWidth: 'auto',
        padding: 0,
      }}
    >
      <div
        className={classNames(
          sharedClasses.modalContainer,
          classes.modalContainer,
        )}
      >
        <div className={sharedClasses.modalHeader}>
          <div className={sharedClasses.modalTitleContainer}>
            <span className={sharedClasses.modalHeaderBigTitle}>
              Загрузка шаблонного договора
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={close}>
            <IcoRemove />
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className={classNames(sharedClasses.contentWrapper, classes.form)}
        >
          <div className={classes.content}>
            <SelectField
              label="Выберите шаблон"
              value={templateId != null ? String(templateId) : null}
              options={templateOptions}
              placeholder="Выберите шаблон"
              loading={templatesLoading}
              className={classes.selectField}
              onChange={handleTemplateChange}
            />

            {constructorLoading ? (
              <div className={classes.loader}>
                <Preloader />
              </div>
            ) : null}

            {constructorData && !constructorLoading ? (
              <>
                <InputWhite
                  title="Название договора"
                  value={documentName}
                  onChange={setDocumentName}
                  placeholder="Введите название договора"
                  className={classes.input}
                />

                {constructorGroups.map((group) => (
                  <ConstructorGroupFields
                    key={group.code}
                    group={group}
                    values={values}
                    invalidFieldCodes={invalidFieldCodes}
                    onChange={handleFieldChange}
                    groupSelector={getGroupSelector(group.code)}
                    isLoading={
                      (parentLoading && PARENT_GROUP_CODES.has(group.code)) ||
                      (productLoading && PRODUCT_GROUP_CODES.has(group.code))
                    }
                  />
                ))}
              </>
            ) : null}
          </div>

          <div className={classes.footer}>
            <Button
              type="submit"
              color="primary"
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
              className={classes.submitButton}
            >
              Сохранить
            </Button>
          </div>
        </form>
      </div>
    </Modal>,
    document.body,
  )
}

export default AddTemplateContractModal
